import React, { useState, useEffect } from 'react';
import { Globe, Sparkles, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { dbManager } from '../services/db_manager';
import { tourService } from '../services/tourService';

const languages = [
  { name: 'English', flag: '🇺🇸', code: 'en' },
  { name: 'Português', flag: '🇧🇷', code: 'pt' },
  { name: 'Français', flag: '🇫🇷', code: 'fr' },
  { name: 'Español', flag: '🇪🇸', code: 'es' },
  { name: 'Italiano', flag: '🇮🇹', code: 'it' },
  { name: 'Deutsch', flag: '🇩🇪', code: 'de' },
  { name: '日本語', flag: '🇯🇵', code: 'ja' },
  { name: '中文', flag: '🇨🇳', code: 'zh' },
  { name: 'Русский', flag: '🇷🇺', code: 'ru' },
  { name: 'Magyar', flag: '🇭🇺', code: 'hu' },
];

const LanguagePickerModal: React.FC = () => {
  const { setTargetLanguage } = useTheme();
  const { user } = useAuth();
  const { profile } = useData();
  const { t } = useTranslation();
  const [selectedCode, setSelectedCode] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Show the modal IF the user is fully logged in, profile is loaded, 
    // AND there is no native_language saved in D1 yet,
    // AND they have not dismissed this picker in the current browser/device session.
    if (!user || (user as any).isGuest || !profile) {
      setVisible(false);
      return;
    }
    
    const profileLang = (profile as any).native_language;
    const isDismissed = localStorage.getItem('libritui_language_picker_dismissed') === 'true';

    if (!profileLang && !isDismissed) {
      setVisible(true);
    } else {
      setVisible(false);
    }
  }, [user, profile]);

  if (!visible) return null;

  const handleClose = () => {
    localStorage.setItem('libritui_language_picker_dismissed', 'true');
    setVisible(false);
  };

  const handleSelect = (code: string) => {
    setSelectedCode(code);
    setIsLoading(true);

    // Save to D1 via profiles upsert
    if (user) {
      dbManager.from('profiles', user.id).upsert({
        id: user.id,
        native_language: code,
        updated_at: new Date().toISOString()
      }).then(() => {
        // Automatically start the onboarding tour for new users
        if ((profile as any).tutorial_completed !== 'yes') {
          tourService.startTour(false, 'HOME');
        }
      }).catch(err => console.warn('[LanguagePicker] D1 save error:', err));
    }

    // Clear session flag
    sessionStorage.removeItem('libritui_show_language_picker');

    // Wait 500ms, then trigger the language switch (Soft Reset)
    setTimeout(() => {
      setTargetLanguage(code);
    }, 500);
  };

  return (
    <div 
      className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
      style={{ backgroundColor: 'rgba(2, 6, 23, 0.92)', backdropFilter: 'blur(24px)' }}
    >
      {!isLoading && (
        <button
          onClick={handleClose}
          className="absolute top-6 right-6 p-3 text-slate-400 hover:text-white bg-slate-900/50 hover:bg-slate-800/80 border border-slate-800 hover:border-slate-700 rounded-2xl transition-all duration-200 hover:scale-105 active:scale-95 z-50"
          aria-label="Close language selection"
        >
          <X size={20} />
        </button>
      )}

      {!isLoading ? (
        /* Language Selection */
        <div 
          className="w-full max-w-md animate-in fade-in zoom-in-95 duration-500"
          style={{ animation: 'fadeInUp 0.6s cubic-bezier(0.16, 1, 0.3, 1)' }}
        >
          {/* Header */}
          <div className="text-center mb-10 space-y-4">
            <div className="w-20 h-20 mx-auto rounded-full flex items-center justify-center mb-6"
                 style={{ background: 'linear-gradient(135deg, rgba(99,102,241,0.2), rgba(168,85,247,0.2))' }}>
              <Globe className="w-10 h-10 text-tintd-lavender" style={{ animation: 'float 3s ease-in-out infinite' }} />
            </div>
            <h1 className="text-2xl font-medium text-white tracking-[-0.03em]">
              {t('language_picker.title', 'Qual é o seu idioma nativo?')}
            </h1>
            <p className="text-sm text-slate-400 font-medium leading-relaxed max-w-xs mx-auto">
              {t('language_picker.subtitle', 'Escolha seu idioma para a melhor experiência de leitura')}
            </p>
          </div>

          {/* Language Grid */}
          <div className="grid grid-cols-2 gap-3">
            {languages.map((lang, i) => (
              <button
                key={lang.code}
                onClick={() => handleSelect(lang.code)}
                className="group flex items-center gap-3 px-5 py-4 rounded-2xl border border-slate-800 hover:border-tintd-lavender/50 bg-slate-900/50 hover:bg-tintd-lavender/10 transition-all duration-300 hover:scale-[1.03] active:scale-95"
                style={{ animation: `fadeInUp 0.4s cubic-bezier(0.16, 1, 0.3, 1) ${i * 0.06}s both` }}
              >
                <span className="text-2xl">{lang.flag}</span>
                <span className="text-sm font-bold text-slate-200 group-hover:text-white transition-colors">{lang.name}</span>
              </button>
            ))}
          </div>
        </div>
      ) : (
        /* Loading State */
        <div 
          className="w-full max-w-sm text-center space-y-8"
          style={{ animation: 'fadeInUp 0.5s cubic-bezier(0.16, 1, 0.3, 1)' }}
        >
          {/* Animated Loader */}
          <div className="relative w-24 h-24 mx-auto">
            <div className="absolute inset-0 rounded-full" 
                 style={{ 
                   background: 'conic-gradient(from 0deg, transparent, rgba(99,102,241,0.6), transparent)',
                   animation: 'spin 1.5s linear infinite'
                 }} />
            <div className="absolute inset-[3px] rounded-full bg-slate-950 flex items-center justify-center">
              <span className="text-4xl" style={{ animation: 'pulse 1s ease-in-out infinite' }}>
                {languages.find(l => l.code === selectedCode)?.flag || '🌐'}
              </span>
            </div>
          </div>

          <div className="space-y-3">
            <h2 className="text-xl font-semibold text-white tracking-tight" style={{ animation: 'pulse 2s ease-in-out infinite' }}>
              {t('language_picker.loading', 'Carregando seu idioma nativo...')}
            </h2>
            <p className="text-xs text-slate-500 font-medium leading-relaxed">
              {t('language_picker.wait', 'Para uma melhor experiência, aguarde um segundo :)')}
            </p>
          </div>

          {/* Tip */}
          <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-2xl bg-slate-800/40 border border-slate-800 mx-auto max-w-xs">
            <Sparkles size={12} className="text-tintd-lavender shrink-0" />
            <p className="text-[10px] text-slate-400 font-normal uppercase font-mono tracking-[0.14em] leading-snug">
              {t('language_picker.tip', 'Você pode alterar as configurações de idioma em preferências → tradução')}
            </p>
          </div>
        </div>
      )}

      {/* Injected animations */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
};

export default LanguagePickerModal;
